/**
 * crawlSourceForm — shared, framework-agnostic helpers for the typed crawl-source
 * builder. Extracted from AdminCrawlSources.vue so the guided PipelineWizard and
 * the standalone Crawl-sources page shape a source's config IDENTICALLY (a
 * divergence here would mean a source created via the wizard behaves differently
 * from one created on the page). Pure functions only — no Vue, no `this`.
 */

export const SOURCE_TYPES = [
  { value: 'hackernews',    label: 'HackerNews',    icon: 'newspaper',   desc: 'Warm — Algolia search API' },
  { value: 'reddit',        label: 'Reddit',         icon: 'note-sticky', desc: 'Warm — subreddit or global search' },
  { value: 'linkedin',      label: 'LinkedIn',       icon: 'building',    desc: 'Remote worker — Playwright' },
  { value: 'site_crawl',    label: 'Site crawl',     icon: 'diagram',     desc: 'Cold — seed URLs, one lead/domain' },
  { value: 'local_search',  label: 'Local search',   icon: 'pin',         desc: 'Cold — OpenStreetMap, free' },
  { value: 'google_places', label: 'Google Places',  icon: 'pin',         desc: 'Cold — keyed, billed' },
  { value: 'geo_osm',       label: 'Waterway (geo)', icon: 'droplet',     desc: 'Cold — canal-network radius sweep (Canal Nav)' },
  { value: 'uk_osm_sweep',  label: 'UK national sweep', icon: 'pin',      desc: 'Cold — whole-UK OpenStreetMap tiled sweep, free' },
  { value: 'ch_sic',        label: 'Companies House', icon: 'building',   desc: 'Cold - registered firms by SIC code + area' },
  { value: 'seed',          label: 'Seed',           icon: 'droplet',     desc: 'Fixed test candidates' },
];

// The BROAD OSM top-level keys — the historical default. These stay valid as an
// implicit "everything with a business tag" preset; a VERTICAL (digital agencies,
// cafés, plumbers…) is now expressed with the business-type catalogue below,
// which compiles to key=value selectors like `office=advertising_agency`.
// Mirrors BusinessTypeCatalogue::BROAD_KEYS on the backend.
export const CATEGORY_OPTIONS = ['office', 'shop', 'craft', 'amenity', 'tourism', 'leisure', 'healthcare'];

/**
 * Business-type preset catalogue, served by GET /api/v1/admin/crawl/business-types.
 * Held module-side (rather than threaded through every pure helper) so
 * buildTypedConfig(form) / applyConfigToForm(type, cfg) keep their existing
 * signatures — both are called from three components. Empty until a component
 * loads it; with an empty catalogue every helper degrades to the pre-catalogue
 * behaviour (broad keys only), never to an exception.
 */
let BUSINESS_TYPES = [];

export function setBusinessTypeCatalogue(list) {
  BUSINESS_TYPES = Array.isArray(list) ? list : [];
}

export function businessTypeCatalogue() {
  return BUSINESS_TYPES;
}

export function businessTypeBySlug(slug) {
  return BUSINESS_TYPES.find((p) => p.slug === slug) || null;
}

/** OSM selectors for a list of preset slugs (unknown slugs ignored, deduped). */
export function selectorsForSlugs(slugs) {
  const out = [];
  (Array.isArray(slugs) ? slugs : []).forEach((slug) => {
    const preset = businessTypeBySlug(slug);
    if (!preset || !Array.isArray(preset.osm_selectors)) return;
    preset.osm_selectors.forEach((sel) => { if (!out.includes(sel)) out.push(sel); });
  });
  return out;
}

/** UK SIC 2007 codes for a list of preset slugs (unknown slugs ignored, deduped). */
export function sicCodesForSlugs(slugs) {
  const out = [];
  (Array.isArray(slugs) ? slugs : []).forEach((slug) => {
    const preset = businessTypeBySlug(slug);
    if (!preset || !Array.isArray(preset.sic_codes)) return;
    preset.sic_codes.forEach((c) => { const s = String(c).trim(); if (s && !out.includes(s)) out.push(s); });
  });
  return out;
}

/**
 * The presets a Companies House (ch_sic) sweep can actually search on — the
 * register is queried BY SIC code, so a preset with none is not offerable there.
 * Every preset ships codes today; this keeps the picker honest if one ever
 * doesn't (and while a stale bundle's catalogue is still empty).
 */
export function businessTypesWithSic() {
  return BUSINESS_TYPES.filter((p) => Array.isArray(p.sic_codes) && p.sic_codes.length > 0);
}

/**
 * The OSM `categories` list a form compiles to:
 *   - business types picked → their selectors (the broad chips are ignored, so a
 *     vertical search isn't swamped by "everything tagged office"),
 *   - none picked          → the broad keys the operator ticked (today's behaviour),
 *   - plus any raw key=value lines from the advanced free-entry box.
 */
export function compileCategories(form) {
  const out = [];
  const push = (v) => {
    const s = String(v == null ? '' : v).trim();
    if (s && !out.includes(s)) out.push(s);
  };
  const slugs = Array.isArray(form.business_types) ? form.business_types : [];
  if (slugs.length) selectorsForSlugs(slugs).forEach(push);
  else if (Array.isArray(form.categories)) form.categories.forEach(push);
  String(form.categories_raw || '').split(/[\n,]+/).forEach(push);
  return out;
}

/** True when a compiled list is exactly the broad default set (order-insensitive). */
function isBroadDefault(compiled) {
  return compiled.length === CATEGORY_OPTIONS.length && CATEGORY_OPTIONS.every((k) => compiled.includes(k));
}

/** The strongest coverage warning across the picked presets, or null. */
export function coverageHintFor(slugs) {
  const placesOnly = (Array.isArray(slugs) ? slugs : [])
    .map(businessTypeBySlug)
    .filter((p) => p && p.coverage === 'places');
  if (!placesOnly.length) return null;
  const names = placesOnly.map((p) => p.label).join(', ');
  return `${names} ${placesOnly.length === 1 ? 'is' : 'are'} rarely mapped on OpenStreetMap. `
    + 'Google Places will find far more of them (billed per request).';
}

// CN-1 — Canal Nav category enum the geo_osm sweep can discover (a subset of the
// full frozen enum; trade_boat/brokerage/ecommerce come from later enrichment).
export const CN_CATEGORY_OPTIONS = [
  'marina', 'chandlery', 'hire_boat', 'boatyard', 'services',
  'fuel', 'moorings', 'surveyor', 'pub', 'other',
];

// CN-1 — default per-category radius (metres) shown in the geo_osm editor.
export const CN_DEFAULT_RADII = {
  marina: 400, boatyard: 400, hire_boat: 400, chandlery: 300,
  services: 300, fuel: 250, moorings: 250, pub: 200,
};

// Slice A — recurring re-run cadence. Manual = run only when an operator clicks
// Run (today's behaviour). Automatic re-runs re-fetch from the provider — for
// Google Places that RE-BILLS each sweep, so the UI says so honestly.
export const SCHEDULE_OPTIONS = [
  { value: 'manual',  label: 'Manual' },
  { value: 'hourly',  label: 'Hourly' },
  { value: 'daily',   label: 'Daily' },
  { value: 'weekly',  label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

// config keys owned by the typed form, per type. Anything else round-trips through extraConfig.
// `schedule` is common to every type (a scheduling column carried in config like
// rate_per_min), so splitExtra() adds it universally rather than listing it here.
export const KNOWN_KEYS = {
  hackernews: ['query', 'tags', 'hitsPerPage'],
  reddit: ['query', 'subreddit', 'limit', 'user_agent'],
  linkedin: ['query', 'limit', 'auto_enrich'],
  site_crawl: ['seed_urls', 'max', 'auto_enrich'],
  local_search: ['location', 'lat', 'lon', 'radius_miles', 'categories', 'business_type_slugs', 'max', 'include_no_website', 'overpass_url', 'auto_enrich'],
  google_places: ['query', 'location', 'lat', 'lon', 'radius_miles', 'max', 'auto_enrich'],
  geo_osm: ['seed_path', 'anchor_srcs', 'categories', 'radius_default_m', 'radius_by_category', 'tile_deg', 'max_tiles_per_run', 'overpass_endpoint', 'delay_ms', 'user_agent', 'auto_enrich'],
  uk_osm_sweep: ['tile_deg', 'max_tiles_per_run', 'categories', 'business_type_slugs', 'include_no_website', 'refresh', 'delay_ms', 'overpass_url', 'user_agent', 'auto_enrich'],
  ch_sic: ['business_types', 'business_type_slugs', 'sic_codes', 'location', 'company_status', 'max', 'size', 'max_requests', 'auto_enrich'],
  seed: [],
};

export const numOr = (v, d) => {
  if (v === '' || v === null || v === undefined) return d;
  const n = Number(v);
  return Number.isNaN(n) ? d : n;
};

export function defaultFormForType(type) {
  return {
    type,
    name: '',
    query: type === 'google_places' ? 'businesses' : '',
    tags: 'story',
    hitsPerPage: 20,
    subreddit: '',
    limit: type === 'reddit' ? 25 : '',
    user_agent: '',
    seed_urls: '',
    max: type === 'site_crawl' ? 50 : type === 'local_search' ? 100 : type === 'google_places' ? 40 : type === 'ch_sic' ? 200 : '',
    location: '',
    lat: '',
    lon: '',
    useCoords: false,
    radius_miles: 10,
    // Broad OSM keys (used only while no business type is picked).
    categories: [...CATEGORY_OPTIONS],
    // Business-type preset slugs — the vertical picker. Empty = broad search.
    business_types: [],
    // Advanced free-entry: extra raw selectors, one `key=value` (or bare key) per line.
    categories_raw: '',
    include_no_website: true,
    overpass_url: '',
    // CN-1 geo_osm
    radius_default_m: 200,
    max_tiles_per_run: type === 'uk_osm_sweep' ? 40 : 50,
    cn_categories: [...CN_CATEGORY_OPTIONS],
    radius_by_category: { ...CN_DEFAULT_RADII },
    overpass_endpoint: '',
    // Task #49 uk_osm_sweep
    tile_deg: 0.1,
    refresh: true,
    // Phase 2 ch_sic — Companies House by SIC code. Raw codes are the advanced
    // escape hatch when no preset covers the vertical; the request cap keeps a
    // sweep inside the CH key's shared rate budget.
    sic_codes_raw: '',
    company_status: 'active',
    max_requests: 20,
    auto_enrich: type === 'uk_osm_sweep',
    rate_per_min: 30,
    // The national sweep's pace comes from schedule frequency — default it hourly.
    schedule: type === 'uk_osm_sweep' ? 'hourly' : 'manual',
  };
}

const SCHEDULE_VALUES = new Set(SCHEDULE_OPTIONS.map((o) => o.value));
const normSchedule = (v) => (SCHEDULE_VALUES.has(v) ? v : 'manual');

export function buildTypedConfig(form) {
  const t = form.type;
  const cfg = {};
  // Common to all types (incl. seed, which returns early below) — a re-run
  // cadence stored in config; the backend lifts it into the schedule column.
  cfg.schedule = normSchedule(form.schedule);
  if (t === 'hackernews') {
    cfg.query = form.query || '';
    cfg.tags = form.tags || 'story';
    cfg.hitsPerPage = numOr(form.hitsPerPage, 20);
  } else if (t === 'reddit') {
    cfg.query = form.query || '';
    if (form.subreddit && form.subreddit.trim()) cfg.subreddit = form.subreddit.trim();
    cfg.limit = numOr(form.limit, 25);
    if (form.user_agent && form.user_agent.trim()) cfg.user_agent = form.user_agent.trim();
  } else if (t === 'linkedin') {
    cfg.query = form.query || '';
    if (form.limit !== '' && form.limit != null) cfg.limit = numOr(form.limit, 25);
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'site_crawl') {
    cfg.seed_urls = String(form.seed_urls || '').split('\n').map((s) => s.trim()).filter(Boolean).join('\n');
    cfg.max = numOr(form.max, 50);
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'local_search') {
    if (form.useCoords) {
      if (form.lat !== '' && form.lat != null) cfg.lat = numOr(form.lat, 0);
      if (form.lon !== '' && form.lon != null) cfg.lon = numOr(form.lon, 0);
    } else {
      cfg.location = form.location || '';
    }
    cfg.radius_miles = numOr(form.radius_miles, 10);
    cfg.categories = compileCategories(form);
    // Store the SELECTORS (the backend stays dumb); the slugs ride along purely
    // so the form can re-open on the vertical the operator actually chose.
    if (Array.isArray(form.business_types) && form.business_types.length) {
      cfg.business_type_slugs = form.business_types.slice();
    }
    cfg.max = numOr(form.max, 100);
    cfg.include_no_website = !!form.include_no_website;
    if (form.overpass_url && form.overpass_url.trim()) cfg.overpass_url = form.overpass_url.trim();
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'google_places') {
    cfg.query = form.query || 'businesses';
    if (form.useCoords) {
      if (form.lat !== '' && form.lat != null) cfg.lat = numOr(form.lat, 0);
      if (form.lon !== '' && form.lon != null) cfg.lon = numOr(form.lon, 0);
    } else {
      cfg.location = form.location || '';
    }
    cfg.radius_miles = numOr(form.radius_miles, 10);
    cfg.max = Math.min(numOr(form.max, 40), 60);
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'geo_osm') {
    cfg.radius_default_m = numOr(form.radius_default_m, 200);
    cfg.max_tiles_per_run = numOr(form.max_tiles_per_run, 50);
    // Only pass categories when the operator narrowed the sweep (empty = all).
    if (Array.isArray(form.cn_categories) && form.cn_categories.length && form.cn_categories.length < CN_CATEGORY_OPTIONS.length) {
      cfg.categories = form.cn_categories.slice();
    }
    // Per-category radius overrides (numeric only).
    const rbc = {};
    Object.keys(form.radius_by_category || {}).forEach((k) => {
      const n = Number(form.radius_by_category[k]);
      if (!Number.isNaN(n) && n > 0) rbc[k] = n;
    });
    if (Object.keys(rbc).length) cfg.radius_by_category = rbc;
    if (form.overpass_endpoint && form.overpass_endpoint.trim()) cfg.overpass_endpoint = form.overpass_endpoint.trim();
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'uk_osm_sweep') {
    cfg.tile_deg = numOr(form.tile_deg, 0.1);
    cfg.max_tiles_per_run = numOr(form.max_tiles_per_run, 40);
    // Only pass categories when the operator narrowed the sweep (empty = all).
    const sweepCats = compileCategories(form);
    if (sweepCats.length && !isBroadDefault(sweepCats)) {
      cfg.categories = sweepCats;
      if (Array.isArray(form.business_types) && form.business_types.length) {
        cfg.business_type_slugs = form.business_types.slice();
      }
    }
    cfg.include_no_website = !!form.include_no_website;
    cfg.refresh = form.refresh !== false;
    if (form.overpass_url && form.overpass_url.trim()) cfg.overpass_url = form.overpass_url.trim();
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'ch_sic') {
    // The register is searched BY SIC code: preset slugs (the adapter unions
    // their codes) plus any raw codes typed under advanced. Location is matched
    // by Companies House against the registered office - we never re-filter it.
    if (Array.isArray(form.business_types) && form.business_types.length) {
      cfg.business_types = form.business_types.slice();
    }
    const rawSic = String(form.sic_codes_raw || '')
      .split(/[\s,]+/).map((s) => s.trim()).filter((s) => /^\d{4,5}$/.test(s));
    if (rawSic.length) cfg.sic_codes = rawSic;
    cfg.location = form.location || '';
    cfg.company_status = form.company_status || 'active';
    cfg.max = Math.min(numOr(form.max, 200), 500);
    cfg.max_requests = Math.min(numOr(form.max_requests, 20), 30);
    cfg.auto_enrich = !!form.auto_enrich;
  } else if (t === 'seed') {
    return cfg; // {} — seed emits fixed test candidates, no config at all
  }
  cfg.rate_per_min = numOr(form.rate_per_min, 30);
  return cfg;
}

/**
 * Reverse of compileCategories(): split a stored `categories` list back into the
 * three controls that produced it — picked business types (from the slugs the
 * form stored alongside), broad key chips, and raw key=value free entry. A
 * config written before this feature has no slugs and no `=`, so it lands
 * entirely on the broad chips exactly as it used to.
 */
function applyCategoriesToForm(out, cfg) {
  const cats = Array.isArray(cfg.categories) ? cfg.categories.map((c) => String(c)) : [];
  const slugs = Array.isArray(cfg.business_type_slugs)
    ? cfg.business_type_slugs.filter((s) => typeof s === 'string' && s)
    : [];
  if (slugs.length) {
    out.business_types = slugs.slice();
    const covered = new Set(selectorsForSlugs(slugs));
    out.categories_raw = cats.filter((c) => !covered.has(c)).join('\n');
    return;
  }
  if (!cats.length) return;
  out.categories = cats.filter((c) => !c.includes('='));
  out.categories_raw = cats.filter((c) => c.includes('=')).join('\n');
}

export function applyConfigToForm(type, config) {
  const cfg = config || {};
  const out = defaultFormForType(type);
  if (type === 'hackernews') {
    if (cfg.query != null) out.query = String(cfg.query);
    if (cfg.tags != null) out.tags = String(cfg.tags);
    if (cfg.hitsPerPage != null) out.hitsPerPage = Number(cfg.hitsPerPage);
  } else if (type === 'reddit') {
    if (cfg.query != null) out.query = String(cfg.query);
    if (cfg.subreddit != null) out.subreddit = String(cfg.subreddit);
    if (cfg.limit != null) out.limit = Number(cfg.limit);
    if (cfg.user_agent != null) out.user_agent = String(cfg.user_agent);
  } else if (type === 'linkedin') {
    if (cfg.query != null) out.query = String(cfg.query);
    if (cfg.limit != null) out.limit = Number(cfg.limit);
    out.auto_enrich = !!cfg.auto_enrich;
  } else if (type === 'site_crawl') {
    if (Array.isArray(cfg.seed_urls)) out.seed_urls = cfg.seed_urls.join('\n');
    else if (typeof cfg.seed_urls === 'string') out.seed_urls = cfg.seed_urls.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean).join('\n');
    if (cfg.max != null) out.max = Number(cfg.max);
    out.auto_enrich = !!cfg.auto_enrich;
  } else if (type === 'local_search' || type === 'google_places') {
    if (cfg.query != null) out.query = String(cfg.query);
    if (cfg.location != null) out.location = String(cfg.location);
    if (cfg.lat != null) out.lat = Number(cfg.lat);
    if (cfg.lon != null) out.lon = Number(cfg.lon);
    out.useCoords = cfg.lat != null && cfg.lon != null && (cfg.location == null || cfg.location === '');
    if (cfg.radius_miles != null) out.radius_miles = Number(cfg.radius_miles);
    if (cfg.max != null) out.max = Number(cfg.max);
    out.auto_enrich = !!cfg.auto_enrich;
    if (type === 'local_search') {
      applyCategoriesToForm(out, cfg);
      out.include_no_website = cfg.include_no_website != null ? !!cfg.include_no_website : true;
      if (cfg.overpass_url != null) out.overpass_url = String(cfg.overpass_url);
    }
  } else if (type === 'geo_osm') {
    if (cfg.radius_default_m != null) out.radius_default_m = Number(cfg.radius_default_m);
    if (cfg.max_tiles_per_run != null) out.max_tiles_per_run = Number(cfg.max_tiles_per_run);
    if (Array.isArray(cfg.categories) && cfg.categories.length) out.cn_categories = cfg.categories.slice();
    if (cfg.radius_by_category && typeof cfg.radius_by_category === 'object') {
      out.radius_by_category = { ...CN_DEFAULT_RADII, ...cfg.radius_by_category };
    }
    if (cfg.overpass_endpoint != null) out.overpass_endpoint = String(cfg.overpass_endpoint);
    out.auto_enrich = !!cfg.auto_enrich;
  } else if (type === 'ch_sic') {
    const slugs = Array.isArray(cfg.business_types) ? cfg.business_types : cfg.business_type_slugs;
    if (Array.isArray(slugs)) out.business_types = slugs.filter((s) => typeof s === 'string' && s);
    if (Array.isArray(cfg.sic_codes)) out.sic_codes_raw = cfg.sic_codes.map(String).join('\n');
    else if (typeof cfg.sic_codes === 'string') out.sic_codes_raw = cfg.sic_codes;
    if (cfg.location != null) out.location = String(cfg.location);
    if (cfg.company_status != null) {
      out.company_status = Array.isArray(cfg.company_status) ? cfg.company_status.join(',') : String(cfg.company_status);
    }
    if (cfg.max != null) out.max = Number(cfg.max);
    if (cfg.max_requests != null) out.max_requests = Number(cfg.max_requests);
    out.auto_enrich = !!cfg.auto_enrich;
  } else if (type === 'uk_osm_sweep') {
    if (cfg.tile_deg != null) out.tile_deg = Number(cfg.tile_deg);
    if (cfg.max_tiles_per_run != null) out.max_tiles_per_run = Number(cfg.max_tiles_per_run);
    if (Array.isArray(cfg.categories) && cfg.categories.length) applyCategoriesToForm(out, cfg);
    out.include_no_website = cfg.include_no_website != null ? !!cfg.include_no_website : true;
    out.refresh = cfg.refresh != null ? !!cfg.refresh : true;
    if (cfg.overpass_url != null) out.overpass_url = String(cfg.overpass_url);
    out.auto_enrich = !!cfg.auto_enrich;
  }
  if (type !== 'seed' && cfg.rate_per_min != null) out.rate_per_min = Number(cfg.rate_per_min);
  if (cfg.schedule != null) out.schedule = normSchedule(cfg.schedule);
  return out;
}

export function splitExtra(type, config) {
  const known = new Set(KNOWN_KEYS[type] || []);
  if (type !== 'seed') known.add('rate_per_min');
  known.add('schedule'); // common to all types; owned by the form, not extraConfig
  const extra = {};
  Object.keys(config || {}).forEach((k) => { if (!known.has(k)) extra[k] = config[k]; });
  return extra;
}

/**
 * Lock state for a source-type tile, given the tenant's crawl_types gate map from
 * GET /api/v1/me/usage (slice 5 §3). A tile is LOCKED only when the map lists the
 * type as disabled; types absent from the map (ungated infra — hackernews, reddit,
 * site_crawl, seed) and everything when the map hasn't loaded stay unlocked
 * (fail-open — never block an operator on a slow/failed usage read).
 *
 * @param {Object} crawlTypes  { type: { enabled, unlocked_by } }
 * @param {string} typeValue
 * @returns {{ locked: boolean, plan: (string|null) }}
 */
export function crawlTypeLock(crawlTypes, typeValue) {
  const info = crawlTypes && crawlTypes[typeValue];
  if (info && info.enabled === false) {
    return { locked: true, plan: info.unlocked_by || null };
  }
  return { locked: false, plan: null };
}

/** Client-side "can this source be created?" gate — mirrors the page's canSubmit. */
export function crawlFormValid(form) {
  if (!form.name || !form.name.trim()) return false;
  const t = form.type;
  if (t === 'hackernews' || t === 'reddit' || t === 'linkedin') {
    return !!(form.query && form.query.trim());
  }
  if (t === 'site_crawl') return !!(form.seed_urls && form.seed_urls.trim());
  if (t === 'local_search' || t === 'google_places') {
    if (form.useCoords) return form.lat !== '' && form.lat != null && form.lon !== '' && form.lon != null;
    return !!(form.location && form.location.trim());
  }
  if (t === 'ch_sic') {
    // No codes = nothing to search the register by; no location = the whole UK
    // for that SIC, which is never what the operator meant here.
    const hasCodes = (Array.isArray(form.business_types) && form.business_types.length > 0)
      || /\d{4,5}/.test(String(form.sic_codes_raw || ''));
    return hasCodes && !!(form.location && form.location.trim());
  }
  return true; // seed
}
